import { useEffect, useState } from 'react';
import { Send, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

type Audience = 'single' | 'all' | 'free' | 'monthly' | 'annual' | 'churn_risk';

const AUDIENCE_LABELS: Record<Audience, string> = {
  single:     'Usuário específico',
  all:        'Todos os usuários',
  free:       'Plano Grátis',
  monthly:    'Plano Mensal',
  annual:     'Plano Anual',
  churn_risk: 'Risco de Churn (pagantes inativos 30d+)',
};

const EMAIL_TEMPLATES = [
  { id: 'premium_welcome', name: 'Boas-vindas Premium', desc: 'Enviado quando usuário assina um plano pago. Variáveis: {{name}}, {{productName}}' },
  { id: 'weekly_forecast', name: 'Previsão Semanal', desc: 'Enviado semanalmente pelo cron. Variáveis: {{sign}}, {{forecast}}' },
  { id: 'referral_bonus',  name: 'Bônus de Indicação', desc: 'Enviado ao referrer quando indicado converte. Variáveis: {{name}}, {{referredName}}, {{bonusDays}}' },
  { id: 'payment_failed',  name: 'Pagamento Falhou',   desc: 'Enviado quando cobrança é recusada. Variáveis: {{name}}' },
  { id: 'welcome',         name: 'Boas-vindas',        desc: 'Enviado no cadastro. Variáveis: {{name}}' },
  { id: 'password_reset',  name: 'Redefinição de Senha', desc: 'Enviado ao solicitar reset. Variáveis: {{name}}, {{resetUrl}}' },
  { id: 'gift_card',       name: 'Gift Card',           desc: 'Enviado ao presentear. Variáveis: {{giftCode}}, {{plan}}, {{days}}' },
];

interface EmailLog {
  id: string; to_email: string; subject: string; status: string; created_at: string;
}

interface Subscriber {
  id: string; email: string; confirmed: boolean; unsubscribed_at: string | null; created_at: string;
}

export function AdminEmailsPage() {
  const { profile }        = useAuth();
  const [audience, setAudience] = useState<Audience>('single');
  const [toEmail,  setToEmail]  = useState('');
  const [subject,  setSubject]  = useState('');
  const [body,     setBody]     = useState('');
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState<{ count: number } | null>(null);
  const [logs,     setLogs]     = useState<EmailLog[]>([]);
  const [tab,          setTab]         = useState<'compose' | 'history' | 'newsletter' | 'templates'>('compose');
  const [subscribers,  setSubscribers] = useState<Subscriber[]>([]);
  const [subLoading,   setSubLoading]  = useState(false);
  const [previewTpl,   setPreviewTpl]  = useState<typeof EMAIL_TEMPLATES[number] | null>(null);

  // AI suggestion state
  const [theme,     setTheme]     = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => { if (tab === 'history') loadLogs(); }, [tab]);
  useEffect(() => { if (tab === 'newsletter') loadSubscribers(); }, [tab]);

  async function loadSubscribers() {
    setSubLoading(true);
    const { data } = await supabase
      .from('newsletter_subscribers')
      .select('id, email, confirmed, unsubscribed_at, created_at')
      .order('created_at', { ascending: false })
      .limit(200);
    setSubscribers((data ?? []) as Subscriber[]);
    setSubLoading(false);
  }

  async function loadLogs() {
    const { data } = await supabase
      .from('email_send_log')
      .select('id, to_email, subject, status, created_at')
      .order('created_at', { ascending: false })
      .limit(50);
    setLogs((data ?? []) as EmailLog[]);
  }

  async function generateAiSuggestion() {
    if (!theme.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await supabase.functions.invoke('admin-generate-email', {
        body: {
          theme,
          audience: AUDIENCE_LABELS[audience],
          adminId: profile?.id,
        },
      });
      if (data?.data?.subject) setSubject(data.data.subject);
      if (data?.data?.body) setBody(data.data.body);
    } catch {
      // fallback: generate a simple template client-side
      setSubject(`✦ ${theme}`);
      setBody(`<h2 style="color:#1F4E5F">Olá, {{name}}!</h2>\n\n<p>${theme}</p>\n\n<p>Acesse agora o Professya e explore todas as funcionalidades disponíveis para você.</p>\n\n<p>Com carinho,<br>Equipe Professya ✦</p>`);
    }
    setAiLoading(false);
  }

  async function sendEmail() {
    if (!subject.trim() || !body.trim()) return;
    if (audience === 'single' && !toEmail.trim()) return;
    setSending(true);

    let recipientEmails: string[] = [];

    if (audience === 'single') {
      recipientEmails = [toEmail.trim()];
    } else if (audience === 'churn_risk') {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
      const { data } = await supabase
        .from('profiles')
        .select('email')
        .in('subscription_tier', ['monthly', 'annual'])
        .or(`last_login_at.lt.${thirtyDaysAgo},last_login_at.is.null`)
        .not('email', 'is', null)
        .limit(1000);
      recipientEmails = (data ?? []).map(p => p.email).filter(Boolean) as string[];
    } else {
      let q = supabase.from('profiles').select('email').not('email', 'is', null);
      if (audience !== 'all') q = q.eq('subscription_tier', audience);
      const { data } = await q.limit(1000);
      recipientEmails = (data ?? []).map(p => p.email).filter(Boolean) as string[];
    }

    // Send in batches via edge function
    const { data } = await supabase.functions.invoke('admin-send-email', {
      body: { recipients: recipientEmails, subject, body, adminId: profile?.id },
    });

    setSending(false);
    setSent({ count: data?.data?.sent ?? recipientEmails.length });
    setSubject(''); setBody(''); setToEmail('');
  }

  return (
    <div>
      <AdminPageHeader title="E-mails" objective="Envie e-mails para segmentos de usuários (todos, plano específico, em risco de cancelamento). Use {{name}} no corpo para personalizar com o nome do usuário. Monitore o histórico de envios na aba 'Histórico'." />

      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button onClick={() => setTab('compose')}    className={tab === 'compose'    ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>Compor</button>
        <button onClick={() => setTab('history')}    className={tab === 'history'    ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>Histórico</button>
        <button onClick={() => setTab('newsletter')} className={tab === 'newsletter' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <Users size={13} /> Newsletter
        </button>
        <button onClick={() => setTab('templates')} className={tab === 'templates' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>Templates</button>
      </div>

      {tab === 'compose' && (
        <div className="card" style={{ maxWidth: '680px' }}>
          {sent && (
            <div style={{ padding: '0.75rem 1rem', backgroundColor: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 'var(--radius-md)', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#4ade80', fontSize: 'var(--text-label)' }}>✓ Email enviado para <strong>{sent.count.toLocaleString('pt-BR')}</strong> destinatário(s)</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setSent(null)}>✕</button>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Destinatários</label>
            <select className="form-select" value={audience} onChange={e => setAudience(e.target.value as Audience)}>
              {(Object.keys(AUDIENCE_LABELS) as Audience[]).map(k => (
                <option key={k} value={k}>{AUDIENCE_LABELS[k]}</option>
              ))}
            </select>
          </div>

          {audience === 'single' && (
            <div className="form-group">
              <label className="form-label">E-mail do destinatário</label>
              <input type="email" className="form-input" value={toEmail} onChange={e => setToEmail(e.target.value)} placeholder="usuario@exemplo.com" />
            </div>
          )}

          {/* AI suggestion field */}
          <div className="form-group">
            <label className="form-label">Tema / objetivo do e-mail (para IA)</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                className="form-input"
                value={theme}
                onChange={e => setTheme(e.target.value)}
                placeholder="Ex: Incentivar reativação de usuários inativos com desconto de 20%"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!theme.trim() || aiLoading}
                onClick={generateAiSuggestion}
                style={{ whiteSpace: 'nowrap' }}
              >
                {aiLoading ? 'Gerando...' : '✦ Gerar com IA'}
              </button>
            </div>
            <p className="form-hint">A IA gerará assunto e corpo do e-mail com base no tema informado.</p>
          </div>

          <div className="form-group">
            <label className="form-label">Assunto</label>
            <input type="text" className="form-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Ex: ✦ Seu horóscopo mensal chegou" />
          </div>

          <div className="form-group">
            <label className="form-label">Corpo (HTML ou texto)</label>
            <textarea className="form-textarea" value={body} onChange={e => setBody(e.target.value)} rows={10}
              placeholder={'<h1>Olá {{name}},</h1>\n<p>Temos novidades para você no Professya...</p>'} style={{ fontFamily: 'monospace', fontSize: '13px' }} />
            <p className="form-hint">Suporte a HTML. Use {'{{name}}'} para o nome do usuário. Ex: 'Olá, {'{{name}}'}!'</p>
          </div>

          <div style={{padding:'0.6rem 0.875rem', backgroundColor:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.2)', borderRadius:'6px', fontSize:'var(--text-caption)', color:'#fbbf24', marginBottom:'1rem'}}>
            ⚠️ <strong>LGPD:</strong> Envios em massa devem ser feitos apenas para usuários que consentiram em receber comunicações de marketing. Verifique o opt-in antes de enviar.
          </div>

          {/* Live preview */}
          {(subject || body) && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                Pré-visualização
              </p>
              <div style={{
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                backgroundColor: '#fff',
              }}>
                {/* Email header bar */}
                <div style={{ background: '#0F1123', padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                    <strong style={{ color: 'rgba(255,255,255,0.8)' }}>De:</strong> oraculo@professya.com &nbsp;
                    <strong style={{ color: 'rgba(255,255,255,0.8)' }}>Assunto:</strong> {subject || '(sem assunto)'}
                  </p>
                </div>
                {/* Body */}
                <div
                  style={{ padding: '1rem', minHeight: '80px', maxHeight: '300px', overflowY: 'auto', fontSize: '14px', lineHeight: 1.6, color: '#1e293b' }}
                  dangerouslySetInnerHTML={{ __html: body.replace(/\{\{name\}\}/g, 'Beatriz') }}
                />
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Pré-visualização aproximada. {'{{'}name{'}}'} substituído por "Beatriz" como exemplo.
              </p>
            </div>
          )}

          <button className="btn btn-primary btn-sm" onClick={sendEmail}
            disabled={sending || !subject.trim() || !body.trim() || (audience === 'single' && !toEmail.trim())}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Send size={14} /> {sending ? 'Enviando...' : `Enviar para ${AUDIENCE_LABELS[audience]}`}
          </button>
        </div>
      )}

      {tab === 'newsletter' && (
        <div>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div className="card" style={{ flex: 1, minWidth: '140px', textAlign: 'center' }}>
              <p style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--petrol)', margin: '0 0 0.2rem' }}>
                {subscribers.filter(s => !s.unsubscribed_at).length}
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>Ativos</p>
            </div>
            <div className="card" style={{ flex: 1, minWidth: '140px', textAlign: 'center' }}>
              <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#4ade80', margin: '0 0 0.2rem' }}>
                {subscribers.filter(s => s.confirmed && !s.unsubscribed_at).length}
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>Confirmados</p>
            </div>
            <div className="card" style={{ flex: 1, minWidth: '140px', textAlign: 'center' }}>
              <p style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-muted)', margin: '0 0 0.2rem' }}>
                {subscribers.filter(s => s.unsubscribed_at).length}
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>Descadastrados</p>
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {subLoading && <div className="shimmer" style={{ height: '120px' }} />}
            {!subLoading && (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr><th>Email</th><th>Confirmado</th><th>Status</th><th>Inscrito em</th></tr>
                  </thead>
                  <tbody>
                    {subscribers.map(s => (
                      <tr key={s.id}>
                        <td style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>{s.email}</td>
                        <td>
                          <span style={{ fontSize: '11px', color: s.confirmed ? '#4ade80' : 'var(--text-muted)' }}>
                            {s.confirmed ? '✓ Sim' : '✗ Não'}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                            backgroundColor: s.unsubscribed_at ? 'rgba(239,68,68,0.1)' : 'rgba(74,222,128,0.12)',
                            color: s.unsubscribed_at ? '#fca5a5' : '#4ade80' }}>
                            {s.unsubscribed_at ? 'Descadastrado' : 'Ativo'}
                          </span>
                        </td>
                        <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(s.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                      </tr>
                    ))}
                    {subscribers.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                        Nenhum inscrito ainda
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'templates' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Selecione um template para visualizar e enviar. Clique em "Pré-visualizar" para ver como ficará antes de usar.
          </p>
          {EMAIL_TEMPLATES.map(tpl => (
            <div key={tpl.id} className="card" style={{
              border: previewTpl?.id === tpl.id ? '1px solid var(--petrol)' : undefined,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: previewTpl?.id === tpl.id ? '1rem' : 0 }}>
                <div>
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>{tpl.name}</p>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{tpl.desc}</p>
                  <code style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--bg-base)', padding: '2px 6px', borderRadius: '4px' }}>{tpl.id}</code>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPreviewTpl(previewTpl?.id === tpl.id ? null : tpl)}
                  >
                    {previewTpl?.id === tpl.id ? 'Fechar' : 'Pré-visualizar'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setTab('compose');
                      setSubject(`[${tpl.name}]`);
                      setBody(`<!-- Template: ${tpl.id} -->\n<!-- Variáveis: ${tpl.desc.split('. ')[1] ?? ''} -->\n\n<p>Olá, {{name}}!</p>\n\n<p>...</p>\n\n<p>Equipe Professya ✦</p>`);
                      setPreviewTpl(null);
                    }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Usar como base
                  </button>
                </div>
              </div>
              {previewTpl?.id === tpl.id && (
                <div>
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                    Pré-visualização (dados de exemplo)
                  </p>
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: '#fff' }}>
                    <div style={{ background: '#0F1123', padding: '0.75rem 1rem' }}>
                      <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                        <strong style={{ color: 'rgba(255,255,255,0.8)' }}>Assunto:</strong> {tpl.name} &nbsp;
                        <strong style={{ color: 'rgba(255,255,255,0.8)' }}>De:</strong> oraculo@professya.com
                      </p>
                    </div>
                    <div style={{ padding: '1.25rem', fontSize: '14px', color: '#1e293b', lineHeight: 1.7 }}>
                      <p><strong>Olá, Beatriz!</strong></p>
                      <p style={{ color: '#64748b' }}>Template: <code>{tpl.id}</code></p>
                      <p>{tpl.desc.replace('{{name}}', 'Beatriz').replace('{{productName}}', 'Plano Mensal').replace('{{forecast}}', '...').replace('{{referredName}}', 'João').replace('{{bonusDays}}', '7').replace('{{resetUrl}}', '#').replace('{{giftCode}}', 'GIFT-XYZ').replace('{{plan}}', 'mensal').replace('{{days}}', '30')}</p>
                      <p style={{ marginTop: '1rem' }}>Com carinho,<br /><strong>Equipe Professya ✦</strong></p>
                    </div>
                  </div>
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '0.4rem', fontStyle: 'italic' }}>
                    Esta pré-visualização usa dados de exemplo. O conteúdo real será gerado pelo servidor com os dados do destinatário.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'history' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>Data</th><th>Destinatário</th><th>Assunto</th><th>Status</th></tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id}>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(l.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{l.to_email}</td>
                  <td style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>{l.subject}</td>
                  <td>
                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                      backgroundColor: l.status === 'sent' ? 'rgba(74,222,128,0.12)' : 'rgba(239,68,68,0.1)',
                      color: l.status === 'sent' ? '#4ade80' : '#fca5a5' }}>
                      {l.status === 'sent' ? 'Enviado' : 'Erro'}
                    </span>
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                  Nenhum email enviado ainda
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
